package com.siriusxm.pia.views.unifiedaggregator

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.isCompositeEntityId
import com.siriusxm.cmc.utils.isValidGuid
import com.siriusxm.pia.components.ButtonSize
import com.siriusxm.pia.components.button
import com.siriusxm.pia.rest.unifiedaggregator.Entity
import com.siriusxm.pia.rest.unifiedaggregator.asEntities
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.jsonObject
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

data class SearchStatus(
    val loading: Boolean,
    val results: List<Entity>,
    val typeInput: String? = null,
    val query: String? = null
)

/**
 * Renders an entity search input. Executes searches but DOES NOT render results. The caller must
 * render results how it sees fit.
 */
@Composable
fun entitySearch(
    context: AggregatorService, initSearch: String? = null, initType: String? = null,
    showTypes: Boolean = true, cb: (SearchStatus) -> Unit
) {
    var entityInput by remember { mutableStateOf(initSearch) }
    var status by remember { mutableStateOf(SearchStatus(false, emptyList(), initType, initSearch)) }

    LaunchedEffect(initType) {
        status = status.copy(typeInput = initType)
    }

    LaunchedEffect(status.typeInput, status.query) {
        if (status.query != null && status.typeInput != null) {
            status = status.copy(loading = true)
            var searchResults: List<JsonObject>? = null
            // first check for a guid
            if (isValidGuid(entityInput.orEmpty())) {
                try {
                    val sourceId = context.clientApi.getMapping(entityInput!!).sourceId
                    searchResults = context.clientApi.getEntityById(sourceId).map {
                        it.jsonObject
                    }
                } catch (e: Throwable) {
                    e.printStackTrace()
                }
            } else if (entityInput.isCompositeEntityId()) {
                try {
                    searchResults = context.clientApi.getEntityById(entityInput!!).map { it.jsonObject }
                        .asEntities().filter { status.typeInput == null || it.type == status.typeInput }
                        .map { it.data }
                } catch (e: Throwable) {
                    e.printStackTrace()
                }
            } else {
                val prefix = entityInput?.substringBefore(":", "").orEmpty()
                if (prefix.length in 2..4 && prefix.uppercase() == prefix) {
                    searchResults = context.clientApi.getEntityById(entityInput!!).map { it.jsonObject }
                } else {
                    searchResults =
                        context.clientApi.searchEntities(
                            query = entityInput!!,
                            types = status.typeInput?.let { listOf(it) }?.joinToString(",")
                        ).entities.mapNotNull { it as? JsonObject }
                }
            }
            status = status.copy(
                loading = false,
                results = searchResults.orEmpty().asEntities()
            )
        } else {
            status = status.copy(results = emptyList())
        }
    }

    fun submitSearch() {
        status = status.copy(query = entityInput)
    }

    LaunchedEffect(status) {
        cb(status)
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Stretch)
            gap(1.em)
            marginTop(1.em)
        }
    }) {
        Div({
            style {
                flexGrow(1)
            }
        }) {
            Input(InputType.Text) {
                style {
                    width(100.percent)
                }
                placeholder(
                    status.typeInput?.let { "Search for $it entities by id or text" }
                        ?: "Search for entities by id or text"
                )
                value(entityInput ?: "")
                onInput {
                    entityInput = it.value.ifBlank { null }
                }
                onKeyDown { event ->
                    if (event.key == "Enter") {
                        submitSearch()
                    }
                }
            }
        }

        if (showTypes) {
            Div({
                style {
                    flex(0, 0, 250.px)
                }
            }) {
                Select({
                    style { width(100.percent) }
                    onChange {
                        status = status.copy(typeInput = it.value?.takeIf { it != "any" })
                    }
                }) {
                    Option("any", {
                        if (status.typeInput == null) selected()
                    }) {
                        Text("All Types")
                    }
                    context.entityTypes.sortedBy { it.type }.forEach {
                        Option(it.type, {
                            if (it.type == status.typeInput) selected()
                        }) {
                            Text(it.type)
                        }
                    }
                }
            }
        }

        button {
            title = "Search"
            primary = true
            size = ButtonSize.INPUT_INLINE
            progress = if (status.loading) true else null
            action {
                submitSearch()
            }
        }
    }
}